import { PLATFORM_ID, Pipe, PipeTransform, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
// Plain `dompurify`, not `isomorphic-dompurify`: the isomorphic build pulls jsdom into the SSR
// bundle, which crashes on the server and made every page using this pipe render empty for Google
// (home, FAQ, sub-category and product pages). On the server we let Angular's own sanitizer handle
// [innerHTML] instead (see transform()).
import DOMPurify from 'dompurify';

/** Named entities the CMS output actually contains; everything else numeric is handled generically. */
const NAMED_ENTITIES: Record<string, string> = {
  amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ',
  ndash: '–', mdash: '—', lsquo: '‘', rsquo: '’',
  ldquo: '“', rdquo: '”', hellip: '…', copy: '©', reg: '®', trade: '™'
};

// DOMPurify only works with a real DOM; on the server `isSupported` is false.
if (DOMPurify.isSupported) {
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A' && node.getAttribute('target') === '_blank') {
      node.setAttribute('rel', 'noopener noreferrer');
    }
  });
}

@Pipe({ name: 'safehtml', standalone: true })
export class SafehtmlPipe implements PipeTransform {
  private sanitizer = inject(DomSanitizer);
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  transform(html: string | null | undefined): SafeHtml | string {
    if (!html) return '';
    if (!this.isBrowser || !DOMPurify.isSupported) {
      // Server: return a plain string (not bypassed), so Angular sanitizes it when it is bound to
      // [innerHTML]. The text and links still reach the server HTML that Google reads.
      return this.decodeEntities(html);
    }
    const clean = DOMPurify.sanitize(this.decodeEntities(html), {
      ADD_ATTR: ['target'],
      FORBID_TAGS: ['style', 'iframe', 'form', 'input', 'button'],
    });
    return this.sanitizer.bypassSecurityTrustHtml(clean);
  }

  /**
   * Decodes HTML entities once. Uses a <textarea> in the browser and a regex fallback on the
   * server, where `document` is not defined.
   */
  private decodeEntities(value: string): string {
    if (typeof document !== 'undefined') {
      const txt = document.createElement('textarea');
      txt.innerHTML = value;
      return txt.value;
    }
    return value.replace(/&(#x[0-9a-f]+|#\d+|[a-z][a-z0-9]*);/gi, (match, entity: string) => {
      if (entity[0] === '#') {
        const code = entity[1].toLowerCase() === 'x'
          ? parseInt(entity.slice(2), 16)
          : parseInt(entity.slice(1), 10);
        return code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : match;
      }
      return NAMED_ENTITIES[entity.toLowerCase()] ?? match;
    });
  }
}